import { cn } from '@/lib/utils';
import { useAppTheme } from '@/providers/theme-provider';
import React, { useState } from 'react';
import {
  Platform,
  Text,
  TextInput as RNTextInput,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

type RNTextInputProps = React.ComponentPropsWithoutRef<typeof RNTextInput>;

export interface TextInputProps extends Omit<RNTextInputProps, 'style'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'default' | 'lg';
  multilineMinHeight?: number;
  status?: 'error' | 'success';
  containerClassName?: string;
  inputContainerClassName?: string;
  inputContainerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
}

const SIZE_CLASSES = {
  sm: 'h-9',
  default: 'h-12',
  lg: 'h-14',
} as const;

export const TextInput = React.forwardRef<
  React.ElementRef<typeof RNTextInput>,
  TextInputProps
>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      size = 'default',
      status,
      containerClassName,
      inputContainerClassName,
      inputContainerStyle,
      inputStyle,
      multiline,
      multilineMinHeight,
      editable = true,
      onFocus,
      onBlur,
      placeholder,
      ...props
    },
    ref
  ) => {
    const theme = useAppTheme();
    const [isFocused, setIsFocused] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);

    const hasError = !!error;

    const borderClass =
      status === 'error' || hasError
        ? 'border-error'
        : status === 'success'
          ? 'border-success'
          : isFocused
            ? 'border-primary'
            : 'border-border';

    const handleFocus = (e: any) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: any) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <View className={containerClassName}>
        {label && (
          <Text
            className={cn(
              'text-sm font-semibold mb-1.5 px-1',
              hasError ? 'text-error' : 'text-muted-foreground'
            )}
          >
            {label}
          </Text>
        )}

        <View
          className={cn(
            'flex-row items-center border rounded-[10px] px-3.5 bg-surface',
            borderClass,
            multiline ? 'items-start' : SIZE_CLASSES[size],
            inputContainerClassName,
          )}
          style={inputContainerStyle}
        >
          {leftIcon && (
            <View style={{ marginRight: 8, ...(multiline && { paddingTop: 12 }) }}>
              {leftIcon}
            </View>
          )}

          <RNTextInput
            ref={ref}
            editable={editable}
            multiline={multiline}
            scrollEnabled={multiline ? false : undefined}
            placeholder={placeholder}
            placeholderTextColor={theme.colors.textMuted}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onContentSizeChange={
              multiline
                ? (e) => setContentHeight(e.nativeEvent.contentSize.height)
                : undefined
            }
            accessible
            accessibilityLabel={label || placeholder || 'Text input'}
            accessibilityState={{ disabled: !editable }}
            accessibilityHint={error || helperText}
            style={[
              {
                flex: 1,
                fontSize: 15,
                color: theme.colors.text,
                paddingVertical: multiline ? 12 : 0,
                ...(multiline && {
                  textAlignVertical: 'top' as const,
                  height: Math.max(contentHeight, multilineMinHeight ?? 76),
                }),
                ...(!editable && { opacity: 0.5 }),
                ...(Platform.OS === 'web' && { outline: 'none' }),
              } as any,
              inputStyle,
            ]}
            {...props}
          />

          {rightIcon && (
            <View style={{ marginLeft: 8, ...(multiline && { paddingTop: 12 }) }}>
              {rightIcon}
            </View>
          )}
        </View>

        {(error || helperText) && (
          <Text
            className={cn(
              'text-xs mt-1 px-1',
              hasError ? 'text-error' : 'text-muted-foreground'
            )}
            accessibilityLiveRegion="polite"
          >
            {error || helperText}
          </Text>
        )}
      </View>
    );
  }
);

TextInput.displayName = 'TextInput';
